'use client';

import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useState } from 'react';
import appLogger from '@/utils/logger';


/**
 * Pull the failing request's identity out of an error so a 400 is attributable to
 * an endpoint.
 *
 * Without this, a mutation failure reached Datadog as nothing but
 * "AxiosError: Request failed with status code 400" against a minified stack — an
 * unattributable 400. Workspace 77 hit exactly that on /settings (2026-07-30,
 * 19:00 and 21:00) and the endpoint could not be identified from telemetry at all.
 *
 * Query-string VALUES are deliberately dropped and only the parameter names kept:
 * they routinely carry search terms and recipient addresses, which do not belong
 * in error telemetry. The path plus the parameter shape is enough to identify the
 * call site.
 */
export function describeFailedRequest(error: unknown): Record<string, unknown> {
  const err = error as {
    config?: { method?: string; url?: string; baseURL?: string };
    response?: { status?: number; statusText?: string };
  };
  if (!err?.config && !err?.response) return {};

  const rawUrl = err.config?.url ?? '';
  let requestPath = rawUrl;
  let requestParams: string[] = [];
  try {
    // Relative URLs are the norm here, so parse against a throwaway base.
    const parsed = new URL(rawUrl, err.config?.baseURL || 'https://relative.invalid');
    requestPath = parsed.pathname;
    requestParams = Array.from(parsed.searchParams.keys());
  } catch {
    requestPath = rawUrl.split('?')[0];
  }

  return {
    request_method: err.config?.method?.toUpperCase(),
    request_path: requestPath,
    request_param_names: requestParams.length ? requestParams.join(',') : undefined,
    response_status: err.response?.status,
    response_status_text: err.response?.statusText,
  };
}

/**
 * Server responses that are a state this app is built to render, not a fault.
 *
 * A workspace with no subscription gets `403 NO_SUBSCRIPTION` from
 * `requireActiveSubscription` on every private endpoint. That is the paywall working,
 * and every page the user opens fires several of them — measured on production
 * 2026-09-24, two sessions on brand-new workspaces produced 34 of the 44 "React Query
 * cache error" entries in six hours, 46% of all frontend errors in that window.
 *
 * Reporting a designed state as an error is not a cosmetic problem: it is how real
 * breakage stops being visible. The same shape cost seventeen days of a stale
 * customer registry earlier today, where a job logged its own fatal timeout as a
 * routine skip.
 *
 * Matched narrowly on the server's own error code, never on the status alone — an
 * ordinary 403 means the user was refused something they should have had, and that
 * is a real fault worth keeping loud.
 */
const EXPECTED_ERROR_CODES = new Set(['NO_SUBSCRIPTION']);

export function isExpectedApplicationState(error: unknown): boolean {
  const err = error as { response?: { status?: number; data?: { code?: unknown } } };
  if (err?.response?.status !== 403) return false;
  const code = err.response?.data?.code;
  return typeof code === 'string' && EXPECTED_ERROR_CODES.has(code);
}

const ReactQueryProvider = ({ children }: PropsWithChildren) => {
  const [client] = useState(
    new QueryClient({
      // QueryCache - handles all query errors globally
      queryCache: new QueryCache({
        onError: (error, query) => {
          const context = {
            component: 'ReactQueryProvider',
            source: 'react-query',
            error_type: 'query_error',
            query_key: JSON.stringify(query?.queryKey ?? null),
            ...describeFailedRequest(error),
          };
          // Still logged, just not as a fault — dropping it entirely would trade one
          // blind spot for another.
          if (isExpectedApplicationState(error)) {
            appLogger.info('React Query expected state', context);
            return;
          }
          appLogger.trackError(error, { ...context, message: 'React Query cache error' });
        },
      }),
      // MutationCache - handles all mutation errors globally
      mutationCache: new MutationCache({
        onError: (error, _variables, _context, mutation) => {
          const context = {
            component: 'ReactQueryProvider',
            source: 'react-query',
            error_type: 'mutation_error',
            mutation_key: JSON.stringify(mutation?.options?.mutationKey ?? null),
            ...describeFailedRequest(error),
          };
          if (isExpectedApplicationState(error)) {
            appLogger.info('React Query expected state', context);
            return;
          }
          appLogger.trackError(error, { ...context, message: 'React Query mutation error' });
        },
      }),
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: false,
          // Disable hydration for SSR compatibility
          staleTime: 60 * 1000, // 1 minute
          // CRITICAL FIX: Prevent queries from running during hydration
          // This prevents the infinite loading issue on first access
          refetchOnMount: 'always',
          // Add network status handling
          networkMode: 'online',
          // Prevent background refetches during initial load
          refetchInterval: false,
          refetchIntervalInBackground: false,
        },
      },
    }),
  );

  // TEMP DIAGNOSTIC (workflow-assistant first-message-loss investigation):
  // observerAdded with count===1 fires exactly when a query hook mounts fresh
  // system-wide -- the definitive, component-agnostic signal for "something just
  // remounted", independent of which component it was. Remove once root-caused.
  useEffect(() => {
    const unsubscribe = client.getQueryCache().subscribe((event) => {
      if (event.type === 'observerAdded' && event.query.getObserversCount() === 1) {
        // eslint-disable-next-line no-console
        console.log('[DIAG][QueryCache] FRESH OBSERVER (component just mounted a query hook)', {
          queryKey: event.query.queryKey,
          status: event.query.state.status,
          dataUpdatedAt: new Date(event.query.state.dataUpdatedAt).toISOString(),
          timestamp: new Date().toISOString(),
        });
      } else if (event.type === 'removed') {
        // eslint-disable-next-line no-console
        console.log('[DIAG][QueryCache] QUERY REMOVED FROM CACHE', {
          queryKey: event.query.queryKey,
          timestamp: new Date().toISOString(),
        });
      }
    });
    return unsubscribe;
  }, [client]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};

export default ReactQueryProvider;
